import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import ModalHeader from '@components/modals/ModalHeader';

import ModalContainer from '@/components/modals/ModalContainer';
import CustomDropdown from '@/components/CustomDropdown';
import InputField from '@/components/InputField';
import {
	formatSelectedTaskToEdit,
	formatStageList,
	formatTaskList,
	fromatRequiredIfConditions,
	handleValidateFields
} from '@/features/task-configuration/utils';
import {
	TASK_CREATE_INIIAL_STATE,
	TASK_ENUM,
	TASK_POSITIONS_LIST,
	TASK_STATUS
} from '@/features/task-configuration/constants';
import { useGetEnumsQuery, useGetUserByTypeWithExternalUserQuery } from '@/api/commonApis';
import { useTaskConfigurations } from '@/store/reducers';
import {
	useCreateNewTaskMutation,
	useGetProductDevJourneyQuery,
	useGetTaskStageQuery,
	useGetTaskWithDependencyQuery,
	useUpdateExistingTaskMutation
} from '@/features/task-configuration/api/taskConfigurationApi';
import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import CommonImage from '@/components/CommonImage';

const CreateAndEditTask = ({ onClose, selectedTaskToEdit }) => {
	const { stageList = [], taskList = [] } = useTaskConfigurations();
	const [taskInputValues, setTaskInputValues] = useState({
		...TASK_CREATE_INIIAL_STATE,
		journeyTypeOne: {},
		journeyOne: {},
		journeyTypeTwo: {},
		journeyTwo: {},
		journeyTypeThree: {},
		journeyThree: {},
		additionalJourneys: []
	});
	const [errors, setErrors] = useState(null);
	const [selectedStageTask, setSelectedStageTask] = useState([]);
	const { data: productJourneyList } = useGetProductDevJourneyQuery();
	const { data: taskWithDependency = [] } = useGetTaskWithDependencyQuery();
	const { data: taskConditionList } = useGetEnumsQuery({
		path: TASK_ENUM.COMPLETION_CONDITION_ENUM
	});
	const { data: taskValueTypes } = useGetEnumsQuery({
		path: TASK_ENUM.TASK_VALUE_TYPES_ENUM
	});
	const { data: userList } = useGetUserByTypeWithExternalUserQuery();

	const [createNewTask, { isLoading: taskCreating }] = useCreateNewTaskMutation();
	const [updateExistingTask, { isLoading: taskUpdating }] = useUpdateExistingTaskMutation();
	const { data: productStageList } = useGetTaskStageQuery();

	const handleTaskChange = (key, value) => {
		setTaskInputValues((prev) => ({
			...prev,
			[key]: value
		}));

		// remove error.
		setErrors((prev) => ({
			...prev,
			[key]: ''
		}));
	};

	const handleSubmit = () => {
		const hasErrors = handleValidateFields(taskInputValues, setErrors);
		if (hasErrors) {
			return;
		}

		const reqestBody = {
			taskSerialReferenceId: taskInputValues?.selectedTaskForPosition?.id,
			name: taskInputValues?.taskName,
			taskValueType: taskInputValues?.compleationCondition?.constant,
			serialType: taskInputValues?.taskPosition?.id,
			status: taskInputValues?.taskStatus?.id,
			productDevelopmentStageId: taskInputValues?.stage?.id,
			dependencyLogicType: taskInputValues?.dependencyToggle ? 'OR' : 'AND',
			userTypeList:
				Array.isArray(taskInputValues.whoCanCompleate) && taskInputValues.whoCanCompleate.length > 0
					? taskInputValues.whoCanCompleate.map((item) => item?.id)
					: undefined,
			dependentOnTaskIdList:
				Array.isArray(taskInputValues?.dependency) && taskInputValues.dependency.length > 0
					? taskInputValues.dependency.map((item) => item?.id)
					: undefined,
			taskNotRequiredConditionList:
				Array.isArray(taskInputValues?.requiredCondition) &&
				taskInputValues.requiredCondition.length > 0
					? taskInputValues.requiredCondition.map((item) => item?.id)
					: undefined,
			journeys: (() => {
				const journeysObj = {};
				// Add original 3 journeys
				if (taskInputValues?.journeyOne?.id && taskInputValues?.journeyTypeOne?.value) {
					journeysObj[taskInputValues.journeyOne.id] = taskInputValues.journeyTypeOne.value;
				}
				if (taskInputValues?.journeyTwo?.id && taskInputValues?.journeyTypeTwo?.value) {
					journeysObj[taskInputValues.journeyTwo.id] = taskInputValues.journeyTypeTwo.value;
				}
				if (taskInputValues?.journeyThree?.id && taskInputValues?.journeyTypeThree?.value) {
					journeysObj[taskInputValues.journeyThree.id] = taskInputValues.journeyTypeThree.value;
				}
				// Add additional journeys
				taskInputValues.additionalJourneys.forEach((journey) => {
					if (journey.journey?.id && journey.journeyType?.value) {
						journeysObj[journey.journey.id] = journey.journeyType.value;
					}
				});
				return journeysObj;
			})()
		};

		// Remove keys with undefined, null, or empty string/array values
		const cleanedRequestBody = Object.entries(reqestBody).reduce((acc, [key, value]) => {
			if (
				value !== undefined &&
				value !== null &&
				!(Array.isArray(value) && value.length === 0) &&
				value !== ''
			) {
				acc[key] = value;
			}
			return acc;
		}, {});

		if (selectedTaskToEdit) {
			updateExistingTask({
				body: cleanedRequestBody,
				taskId: selectedTaskToEdit?.task?.id
			})
				.unwrap()
				.then(() => {
					toastSuccess('Task created successfully');
					setTaskInputValues({
						...TASK_CREATE_INIIAL_STATE,
						journeyTypeOne: {},
						journeyOne: {},
						journeyTypeTwo: {},
						journeyTwo: {},
						journeyTypeThree: {},
						journeyThree: {},
						additionalJourneys: []
					});
					onClose();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong');
				});
		} else {
			createNewTask({ body: cleanedRequestBody })
				.unwrap()
				.then(() => {
					toastSuccess('Task created successfully');
					setTaskInputValues({
						...TASK_CREATE_INIIAL_STATE,
						journeyTypeOne: {},
						journeyOne: {},
						journeyTypeTwo: {},
						journeyTwo: {},
						journeyTypeThree: {},
						journeyThree: {},
						additionalJourneys: []
					});
					onClose();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong');
				});
		}
	};

	useEffect(() => {
		if (selectedTaskToEdit) {
			const formatted = formatSelectedTaskToEdit(selectedTaskToEdit?.task, taskList);
			// Extract journeys array and separate first 3 from additional
			const journeys = Array.isArray(formatted.journeys) ? formatted.journeys : [];
			// Get journeys beyond first 3 (index 3, 4, 5, etc.)
			// slice(3) will return empty array if length <= 3, which is correct
			const additionalJourneys = journeys.slice(3);

			// Exclude journeys array from state by creating new object without it
			const formattedWithoutJourneys = Object.fromEntries(
				Object.entries(formatted).filter(([key]) => key !== 'journeys')
			);

			// Build the complete state object
			const updatedState = {
				...TASK_CREATE_INIIAL_STATE,
				...formattedWithoutJourneys,
				// Explicitly set journey fields to ensure they're preserved
				journeyTypeOne: formatted.journeyTypeOne || {},
				journeyOne: formatted.journeyOne || {},
				journeyTypeTwo: formatted.journeyTypeTwo || {},
				journeyTwo: formatted.journeyTwo || {},
				journeyTypeThree: formatted.journeyTypeThree || {},
				journeyThree: formatted.journeyThree || {},
				// Set additional journeys (4th and beyond) - ensure it's always an array
				additionalJourneys: Array.isArray(additionalJourneys) ? additionalJourneys : []
			};

			setTaskInputValues(updatedState);

			// Set selected stage tasks for the dropdown
			const stage = stageList.find(
				(s) => s.stageId === selectedTaskToEdit?.task?.productDevelopmentStageResponse?.id
			);
			setSelectedStageTask(
				stage?.taskResponse?.map((item) => ({
					...item,
					id: item?.id,
					value: item?.name,
					label: item?.name
				})) || []
			);
		} else {
			setTaskInputValues({
				...TASK_CREATE_INIIAL_STATE,
				journeyTypeOne: {},
				journeyOne: {},
				journeyTypeTwo: {},
				journeyTwo: {},
				journeyTypeThree: {},
				journeyThree: {},
				additionalJourneys: []
			});
			setSelectedStageTask([]);
		}
	}, [selectedTaskToEdit, stageList, taskList]);

	const getFilteredStageList = (excludeJourneyId = null) => {
		const selectedValues = [
			taskInputValues.journeyOne?.id,
			taskInputValues.journeyTwo?.id,
			taskInputValues.journeyThree?.id,
			...taskInputValues.additionalJourneys.map((journey) => journey.journey?.id).filter(Boolean)
		].filter((id) => id && id !== excludeJourneyId);

		const stages = productJourneyList
			?.filter((item) => !selectedValues.includes(item?.id))
			?.map((stage) => ({
				...stage,
				value: stage?.name,
				name: stage?.name
			}));
		return stages;
	};

	const addMoreJourney = () => {
		setTaskInputValues((prev) => ({
			...prev,
			additionalJourneys: [...prev.additionalJourneys, { journeyType: {}, journey: {} }]
		}));
	};

	const handleAdditionalJourneyChange = (index, field, value) => {
		setTaskInputValues((prev) => {
			const updatedJourneys = [...prev.additionalJourneys];
			updatedJourneys[index] = {
				...updatedJourneys[index],
				[field]: value
			};
			return {
				...prev,
				additionalJourneys: updatedJourneys
			};
		});

		// Remove error if exists
		setErrors((prev) => ({
			...prev,
			[`additionalJourney_${index}_${field}`]: ''
		}));
	};

	const removeAdditionalJourney = (index) => {
		setTaskInputValues((prev) => {
			const updatedJourneys = prev.additionalJourneys.filter((_, i) => i !== index);
			return {
				...prev,
				additionalJourneys: updatedJourneys
			};
		});
	};

	return (
		<LoaderComponent loading={taskCreating || taskUpdating}>
			<ModalContainer className="create-task-configuration-modal">
				<div className="popup-wrapper">
					<div className="common-popup create-task-configuration-modal body-scroll">
						<ModalHeader
							onClose={onClose}
							title={selectedTaskToEdit ? 'Edit task' : 'Create task'}
						/>
						<div className="common-popup-body scroll-y-label">
							<div className="row">
								<div className="col-md-12">
									<InputField
										type="text"
										name="taskName"
										value={taskInputValues.taskName}
										onChangeHandler={(e) => handleTaskChange('taskName', e.target.value)}
										errors={errors?.taskName}
										labelTitle="Task Name*"
									/>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="collection">Stage*</label>
										<CustomDropdown
											title="Select stage"
											onItemClick={(value) => {
												const findTaskFromStage = taskList.filter(
													(item) =>
														item.productDevelopmentStageResponse?.id === value?.id
												);

												setSelectedStageTask(
													findTaskFromStage?.map((item) => ({
														...item,
														id: item?.id,
														value: item?.name
													}))
												);
												handleTaskChange('stage', value);
											}}
											selectedItem={taskInputValues?.stage?.name}
											items={formatStageList(productStageList)}
										/>
										{errors?.stage && <p className="error">{errors?.stage}</p>}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group two-step-form-group">
										<label htmlFor="collection">Task position*</label>
										<div className="d-flex align-items-center">
											<div className="task-side-step">
												<CustomDropdown
													title="Select"
													onItemClick={(value) =>
														handleTaskChange('taskPosition', value)
													}
													selectedItem={taskInputValues?.taskPosition?.value}
													items={TASK_POSITIONS_LIST}
												/>
											</div>
											<div className="w-100">
												<CustomDropdown
													title="Task Step"
													onItemClick={(value) =>
														handleTaskChange('selectedTaskForPosition', value)
													}
													selectedItem={
														taskInputValues?.selectedTaskForPosition?.name
													}
													items={selectedStageTask}
													isDisabled={
														taskInputValues?.taskPosition?.id === 'FIRST_TASK'
													}
													toolTipLength={28}
												/>
											</div>
										</div>
										{(errors?.taskPosition || errors?.selectedTaskForPosition) && (
											<p className="error">
												{errors?.taskPosition || errors.selectedTaskForPosition}
											</p>
										)}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor>Completion condition*</label>
										<CustomDropdown
											title="Select completion condition"
											onItemClick={(value) =>
												handleTaskChange('compleationCondition', value)
											}
											selectedItem={taskInputValues.compleationCondition?.name}
											items={taskValueTypes?.map((item) => ({
												...item,
												id: item?.constant,
												name: item?.name,
												value: item?.name
											}))}
											toolTipLength={35}
										/>
										{errors?.compleationCondition && (
											<p className="error">{errors?.compleationCondition}</p>
										)}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group no-shadow-react-select">
										<div className="d-flex align-items-center mb-2">
											<label htmlFor>Dependency</label>
											<div className="cursor-pointer mr-1 d-flex align-items-center">
												<CommonImage
													src={
														taskInputValues.dependencyToggle
															? `/icons/toggle-on.svg`
															: `/icons/toggle-off.svg`
													}
													alt="toggle"
													onClick={() => {
														handleTaskChange(
															'dependencyToggle',
															!taskInputValues.dependencyToggle
														);
													}}
												/>
												<span className="ml-2">
													{taskInputValues?.dependencyToggle ? 'OR' : 'AND'}
												</span>
											</div>
										</div>
										<CreatableSelect
											isMulti
											value={taskInputValues.dependency}
											onChange={(value) => handleTaskChange('dependency', value)}
											options={formatTaskList(taskWithDependency).filter(
												(item) => item?.label !== taskInputValues.taskName.trim()
											)}
											isValidNewOption={() => false}
										/>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group no-shadow-react-select">
										<label htmlFor>Who can complete?*</label>
										<CreatableSelect
											isMulti
											value={taskInputValues.whoCanCompleate}
											onChange={(value) => handleTaskChange('whoCanCompleate', value)}
											options={userList?.map((user) => ({
												...user,
												id: user?.value
											}))}
											isValidNewOption={() => false}
										/>
										{errors?.whoCanCompleate && (
											<p className="error">{errors?.whoCanCompleate}</p>
										)}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group no-shadow-react-select">
										<label htmlFor>Not required, if it's</label>
										<CreatableSelect
											isMulti
											value={taskInputValues.requiredCondition}
											onChange={(value) => handleTaskChange('requiredCondition', value)}
											options={fromatRequiredIfConditions(taskConditionList)}
											isValidNewOption={() => false}
										/>
									</div>
								</div>
								{/* JOURNEY TYPE */}
								<div className="w-100 mb-3">
									<div className="col-md-12">
										<div className="form-group two-step-form-group">
											<label htmlFor="collection">Select journey type (1)</label>
											<div className="d-flex align-items-center">
												<div className="task-side-step">
													<CustomDropdown
														title="Type"
														onItemClick={(value) =>
															handleTaskChange('journeyTypeOne', value)
														}
														selectedItem={taskInputValues?.journeyTypeOne?.value}
														items={[
															{ id: 0, value: 'START', name: 'Start' },
															{ id: 1, value: 'MIDDLE', name: 'Middle' },
															{ id: 2, value: 'END', name: 'End' }
														]}
													/>
												</div>
												<div className="w-100">
													<CustomDropdown
														title="Select journey"
														onItemClick={(value) =>
															handleTaskChange('journeyOne', value)
														}
														selectedItem={taskInputValues.journeyOne?.value}
														items={getFilteredStageList(
															taskInputValues.journeyOne?.id
														)}
														toolTipLength={25}
													/>
												</div>
												<div
													className="ml-1 cursor-pointer"
													onClick={() =>
														setTaskInputValues((prev) => ({
															...prev,
															journeyTypeOne: {},
															journeyOne: {}
														}))
													}
												>
													<CommonImage
														src="/icons/close-icon.svg"
														alt="close"
														width={12}
														height={12}
														className="close-icon-image"
													/>
												</div>
											</div>
										</div>
									</div>
									<div className="col-md-12">
										<div className="form-group two-step-form-group">
											<label htmlFor="collection">Select journey type (2)</label>
											<div className="d-flex align-items-center">
												<div className="task-side-step">
													<CustomDropdown
														title="Type"
														onItemClick={(value) =>
															handleTaskChange('journeyTypeTwo', value)
														}
														selectedItem={taskInputValues?.journeyTypeTwo?.value}
														items={[
															{ id: 0, value: 'START', name: 'Start' },
															{ id: 1, value: 'MIDDLE', name: 'Middle' },
															{ id: 2, value: 'END', name: 'End' }
														]}
													/>
												</div>
												<div className="w-100">
													<CustomDropdown
														title="Select journey"
														onItemClick={(value) =>
															handleTaskChange('journeyTwo', value)
														}
														selectedItem={taskInputValues.journeyTwo?.value}
														items={getFilteredStageList(
															taskInputValues.journeyTwo?.id
														)}
														toolTipLength={25}
													/>
												</div>
												<div
													className="ml-1 cursor-pointer"
													onClick={() =>
														setTaskInputValues((prev) => ({
															...prev,
															journeyTypeTwo: {},
															journeyTwo: {}
														}))
													}
												>
													<CommonImage
														src="/icons/close-icon.svg"
														alt="close"
														width={12}
														height={12}
														className="close-icon-image"
													/>
												</div>
											</div>
										</div>
									</div>
									<div className="col-md-12">
										<div className="form-group two-step-form-group">
											<label htmlFor="collection">Select journey type (3)</label>
											<div className="d-flex align-items-center">
												<div className="task-side-step">
													<CustomDropdown
														title="Type"
														onItemClick={(value) =>
															handleTaskChange('journeyTypeThree', value)
														}
														selectedItem={
															taskInputValues?.journeyTypeThree?.value
														}
														items={[
															{ id: 0, value: 'START', name: 'Start' },
															{ id: 1, value: 'MIDDLE', name: 'Middle' },
															{ id: 2, value: 'END', name: 'End' }
														]}
													/>
												</div>
												<div className="w-100">
													<CustomDropdown
														title="Select journey"
														onItemClick={(value) =>
															handleTaskChange('journeyThree', value)
														}
														selectedItem={taskInputValues.journeyThree?.value}
														items={getFilteredStageList(
															taskInputValues.journeyThree?.id
														)}
														toolTipLength={25}
													/>
												</div>
												<div
													className="ml-1 cursor-pointer"
													onClick={() =>
														setTaskInputValues((prev) => ({
															...prev,
															journeyTypeThree: {},
															journeyThree: {}
														}))
													}
												>
													<CommonImage
														src="/icons/close-icon.svg"
														alt="close"
														width={12}
														height={12}
														className="close-icon-image"
													/>
												</div>
											</div>
										</div>
									</div>
									{/* Additional journeys beyond first 3 */}
									{/* Additional journeys beyond first 3 */}
									{Array.isArray(taskInputValues.additionalJourneys) &&
										taskInputValues.additionalJourneys.length > 0 &&
										taskInputValues.additionalJourneys.map((journey, index) => (
											<div
												key={`additional-journey-${index}-${journey.journey?.id || index}`}
												className="col-md-12"
											>
												<div className="form-group two-step-form-group">
													<label htmlFor="collection">
														Select journey type ({index + 4})
													</label>
													<div className="d-flex align-items-center">
														<div className="task-side-step">
															<CustomDropdown
																title="Type"
																onItemClick={(value) =>
																	handleAdditionalJourneyChange(
																		index,
																		'journeyType',
																		value
																	)
																}
																selectedItem={journey.journeyType?.value}
																items={[
																	{ id: 0, value: 'START', name: 'Start' },
																	{
																		id: 1,
																		value: 'MIDDLE',
																		name: 'Middle'
																	},
																	{ id: 2, value: 'END', name: 'End' }
																]}
															/>
														</div>
														<div className="w-100">
															<CustomDropdown
																title="Select journey"
																onItemClick={(value) =>
																	handleAdditionalJourneyChange(
																		index,
																		'journey',
																		value
																	)
																}
																selectedItem={journey.journey?.value}
																items={getFilteredStageList(
																	journey.journey?.id
																)}
																toolTipLength={25}
															/>
														</div>
														<div
															className="ml-1 cursor-pointer"
															onClick={() => removeAdditionalJourney(index)}
														>
															<CommonImage
																src="/icons/close-icon.svg"
																alt="close"
																width={12}
																height={12}
																className="close-icon-image"
															/>
														</div>
													</div>
												</div>
											</div>
										))}
									<div className="pl-3">
										<button className="button outline size32" onClick={addMoreJourney}>
											Add More
										</button>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="collection">Task status</label>
										<CustomDropdown
											title="Select task status"
											onItemClick={(value) => handleTaskChange('taskStatus', value)}
											selectedItem={taskInputValues.taskStatus?.value}
											items={TASK_STATUS}
											isShowSearch={false}
											isDisabled={!selectedTaskToEdit}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button outline size32 ml-2" onClick={onClose}>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={handleSubmit}>
									Submit
								</button>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default CreateAndEditTask;
